import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { readVkPending, VK_REDIRECT_URI_VALUE } from "../../platform/social/vk";
import { loginWithVk, linkVk, VkCredential } from "../../api/socialAuth";
import { completeBridge } from "../../api/authBridge";
import { completeMigrationWithVk } from "../../api/accountMigration";
import "./VkCallback.css";

type Phase = "working" | "done" | "error";

// Rendered at /vk/mobile/callback after VK redirects back. Completes login,
// link, or the Telegram bridge depending on the sessionStorage pending entry.
const VkCallback: React.FC = () => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("working");
  const [message, setMessage] = useState<string>("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const fail = (msg: string) => {
      setMessage(msg);
      setPhase("error");
    };

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const error = params.get("error");
      if (error) {
        return fail(error === "access_denied" ? t("auth.vkCancelled") : t("auth.vkFailed"));
      }
      const code = params.get("code");
      const deviceId = params.get("device_id");
      const state = params.get("state");
      if (!code || !deviceId || !state) return fail(t("auth.vkFailed"));
      const pending = readVkPending();
      if (!pending) return fail(t("auth.vkSessionExpired"));
      if (pending.state !== state) return fail(t("auth.vkFailed"));

      const cred: VkCredential = {
        code,
        deviceId,
        state,
        codeVerifier: pending.codeVerifier,
        redirectUri: VK_REDIRECT_URI_VALUE,
      };

      try {
        if (pending.mode === "migration") {
          if (!pending.migration) return fail(t("auth.vkSessionExpired"));
          await completeMigrationWithVk({
            ...pending.migration,
            vkCredential: cred,
          });
          window.location.assign("/web");
          return;
        }
        if (pending.mode === "link") {
          await linkVk(cred);
          window.location.assign("/web");
          return;
        }
        const resp = await loginWithVk(cred);
        if (pending.bridgeNonce) {
          await completeBridge(pending.bridgeNonce, resp);
          setPhase("done");
          return;
        }
        window.location.assign("/web");
      } catch (e: any) {
        fail(e?.message || t("auth.vkFailed"));
      }
    };

    run();
  }, [t]);

  return (
    <div className="vk-callback">
      <div className="vk-callback-card">
        {phase === "working" && <p data-testid="vk-working">{t("auth.vkCompleting")}</p>}
        {phase === "done" && (
          <p data-testid="vk-done" className="vk-callback-done">{t("auth.bridgeDone")}</p>
        )}
        {phase === "error" && (
          <>
            <p data-testid="vk-error" className="auth-error">{message}</p>
            <button type="button" onClick={() => window.location.assign("/web")}>
              {t("common.cancel")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VkCallback;
